import { useEffect, useState } from 'react';
import { Radio, Save, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { useMutation, useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import {
  CRM_FEATURE_DEFINITIONS,
  CRM_FEATURE_PRESETS,
  normalizeCrmFeatures,
  type CrmFeatureGroup,
  type CrmFeatures,
} from '../../../convex/crmFeatures';
import { Toggle } from '@/components/crm/fields';

type FeatureRows = {
  rows: Array<{ customerId: string; features: CrmFeatures; aiConsentConfirmed: boolean }>;
  capabilities: { transcription: boolean; email: boolean; ai: boolean; lookup: boolean; truecaller: boolean };
};

type LiveStatus = { connections: Array<{ customerId: string; status: string; lastError?: string }> };

const GROUPS: CrmFeatureGroup[] = ['Calls', 'Contacts', 'Insights', 'Integrations', 'AI'];

const REQUIREMENT_LABELS: Record<'transcription' | 'email' | 'lookup' | 'truecaller', string> = {
  transcription: 'a transcription key',
  email: 'email sending',
  lookup: 'a Google Maps or AI gateway key',
  truecaller: 'a Truecaller sign-in',
};

/** Admin Console: which CRM features this customer has (what InspireTel sells them). */
export function CrmFeaturesEditor({ customerId, customerName }: { customerId: string; customerName: string }) {
  const all = useQuery<FeatureRows>(backendApi.crmSettings.listCustomerFeatures, {});
  const setFeatures = useMutation(backendApi.crmSettings.setCustomerFeatures);
  const row = all?.rows.find((item) => item.customerId === customerId);
  const [features, setLocal] = useState<CrmFeatures>(() => normalizeCrmFeatures(row?.features));
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState<LiveStatus['connections'][number] | null>(null);
  const saved = JSON.stringify(normalizeCrmFeatures(row?.features));
  const dirty = JSON.stringify(features) !== saved;

  useEffect(() => {
    setLocal(normalizeCrmFeatures(JSON.parse(saved)));
  }, [saved, customerId]);

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/crm/live-status', { credentials: 'include' })
      .then((response) => (response.ok ? (response.json() as Promise<LiveStatus>) : null))
      .then((status) => {
        if (!cancelled) setLive(status?.connections.find((connection) => connection.customerId === customerId) ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [customerId, saved]);

  const save = async () => {
    setSaving(true);
    try {
      await setFeatures({ customerId, features });
      toast.success(`CRM features saved for ${customerName}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const capabilities = all?.capabilities;
  const liveWanted = features.screenPop || features.missedCallRecovery;

  return (
    <Card animate={false}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">CRM features</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">What {customerName || 'this customer'} gets in the CRM. Users also need the CRM module in their access profile.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(CRM_FEATURE_PRESETS).map(([id, preset]) => (
              <button
                type="button"
                key={id}
                onClick={() => setLocal(normalizeCrmFeatures(Object.fromEntries(CRM_FEATURE_DEFINITIONS.map((feature) => [feature.id, preset.features.includes(feature.id)]))))}
                className="px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {!all && <p className="text-sm text-gray-500">Loading…</p>}
        {all && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {GROUPS.map((group) => (
              <div key={group} className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{group}</p>
                {CRM_FEATURE_DEFINITIONS.filter((feature) => feature.group === group).map((feature) => {
                  const missing = feature.requires ? !capabilities?.[feature.requires] : false;
                  return (
                    <Toggle
                      key={feature.id}
                      checked={features[feature.id]}
                      onChange={(value) => setLocal((current) => ({ ...current, [feature.id]: value }))}
                      label={feature.label}
                      description={
                        feature.description +
                        (missing ? ` Needs ${REQUIREMENT_LABELS[feature.requires!]} on the server first.` : '') +
                        (feature.id === 'aiCallSummaries' && features.aiCallSummaries && !row?.aiConsentConfirmed
                          ? ' Waiting for the customer to confirm recording consent in CRM settings.'
                          : '')
                      }
                      badge={missing ? <span className="ml-1 text-[11px] font-normal text-amber-600">setup needed</span> : undefined}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {liveWanted && (
          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <Radio className="w-3.5 h-3.5" />
            Live PBX connection:{' '}
            {live ? `${live.status}${live.lastError ? ` (${live.lastError})` : ''}` : dirty ? 'starts within a minute of saving' : 'not connected yet'}
          </p>
        )}

        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving…' : 'Save CRM features'}
        </button>
      </div>
    </Card>
  );
}
